const STATE_KEYS = ['series', 'model', 'capacity', 'color', 'variant'];
const PROPERTY_INPUTS = {
  series: 'Chair Series',
  model: 'Chair Model',
  capacity: 'Capacity',
  color: 'Color',
};

const cleanValue = (value) =>
  String(value ?? '')
    .replace(/[\[\]{}"'`]/g, '')
    .trim();

const normalize = (value) =>
  cleanValue(value)
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

const getColorBucket = (value) => {
  const text = normalize(value);
  if (!text) return '';
  if (text.includes('obsidian')) return 'obsidian';
  if (text.includes('graphite')) return 'graphite';
  if (text.includes('glacier')) return 'glacier';
  if (text.includes('midnight black') || text.includes('jet black') || text.includes('onyx black') || text === 'black') {
    return 'black';
  }
  if (
    text.includes('space gray') ||
    text.includes('space grey') ||
    text.includes('silver gray') ||
    text.includes('silver grey') ||
    text.includes('chrome gray') ||
    text.includes('chrome grey') ||
    text === 'gray' ||
    text === 'grey'
  ) {
    return 'grey';
  }
  if (text.includes('moss green') || text.includes('moss') || text.includes('green')) return 'green';
  return text;
};

const KNOWN_COLOR_BUCKETS = new Set(['black', 'grey', 'green', 'obsidian', 'graphite', 'glacier']);
const COLOR_OPTION_ALIASES = ['Color', 'Colour', '×¦×‘×¢'];
const COMPATIBILITY_OPTION_ALIASES = [
  'Compatible Chair Group',
  'Compatible Group',
  'Compatible Chair Model',
  'Compatible Model',
  'Chair Model',
  'Model',
];
const CAPACITY_OPTION_ALIASES = ['Capacity', 'Battery Capacity'];

const normalizeModel = (value) => {
  const text = normalize(value);
  if (!text) return '';
  if (text.includes('omni gen')) return 'omni gen';
  if (text.includes('omni se')) return 'omni se';
  if (text.includes('omni pro')) return 'omni pro';
  if (text.includes('maxis manual') || text === 'manual') return 'maxis manual';
  if (text.includes('maxis electric') || text === 'electric') return 'maxis electric';
  if (text.includes('maxis airflow') || text === 'airflow') return 'maxis airflow';
  if (text === 'omni' || text.startsWith('omni ')) return 'omni';
  return text;
};

const normalizeCapacity = (value) => {
  const text = normalize(value);
  if (!text) return '';
  if (text.includes('3000')) return '3000mah';
  if (text.includes('2200')) return '2200mah';
  return text.replace(/\s+/g, '');
};

const splitCompatibilityTokens = (value) =>
  cleanValue(value)
    .split(/\s*(?:\/|\+|,|&)\s*/g)
    .map((item) => normalizeModel(item))
    .filter(Boolean);

const getVariantOptionValues = (variant) =>
  [variant?.option1, variant?.option2, variant?.option3].map((value) => cleanValue(value));

const getOptionNames = (data) =>
  Array.isArray(data?.optionNames) ? data.optionNames.map((name) => normalize(name)) : [];

const getOptionIndex = (data, aliases) => {
  const optionNames = getOptionNames(data);
  const normalizedAliases = aliases.map((alias) => normalize(alias)).filter(Boolean);
  return optionNames.findIndex((optionName) =>
    normalizedAliases.some((alias) => optionName === alias || optionName.includes(alias) || alias.includes(optionName))
  );
};

const getVariantOptionValue = (data, variant, aliases) => {
  const optionIndex = getOptionIndex(data, aliases);
  if (optionIndex === -1) return '';
  const optionValues = getVariantOptionValues(variant);
  return cleanValue(optionValues[optionIndex] || '');
};

const hasCompatibilityOption = (data) => getOptionIndex(data, COMPATIBILITY_OPTION_ALIASES) !== -1;
const hasCapacityOption = (data) => getOptionIndex(data, CAPACITY_OPTION_ALIASES) !== -1;

const getVariantCompatibilityValue = (data, variant) => {
  const compatibilityValue = getVariantOptionValue(data, variant, COMPATIBILITY_OPTION_ALIASES);
  if (compatibilityValue) return compatibilityValue;

  if (Array.isArray(data?.optionNames) && data.optionNames.length) {
    return '';
  }

  return getVariantOptionValues(variant).filter(Boolean).find((value) => !KNOWN_COLOR_BUCKETS.has(getColorBucket(value))) || '';
};

const getVariantCompatibilityTokens = (data, variant) => splitCompatibilityTokens(getVariantCompatibilityValue(data, variant));

const getVariantColor = (data, variant) => {
  const directColor = getVariantOptionValue(data, variant, COLOR_OPTION_ALIASES);
  if (directColor) return directColor;

  const fallbackColor = getVariantOptionValues(variant).filter(Boolean).find((value) => KNOWN_COLOR_BUCKETS.has(getColorBucket(value)));
  if (fallbackColor) return fallbackColor;

  return cleanValue(variant?.title || '');
};

const getVariantCapacity = (data, variant) => getVariantOptionValue(data, variant, CAPACITY_OPTION_ALIASES);

const splitValues = (value) =>
  cleanValue(value)
    .split(',')
    .map((item) => cleanValue(item))
    .filter(Boolean);

const dedupeByKey = (items, keyFn) => {
  const seen = new Set();
  const result = [];
  items.forEach((item) => {
    const key = keyFn(item);
    if (!key || seen.has(key)) return;
    seen.add(key);
    result.push(item);
  });
  return result;
};

const shouldDebug = () =>
  window.__libernovoProductContextSwitcherDebug__ === true ||
  new URLSearchParams(window.location.search).has('debug-switcher');

const debugLog = (...args) => {
  if (shouldDebug()) {
    console.info('[product-context-switcher]', ...args);
  }
};

const executeScripts = (container) => {
  if (!container) return;

  container.querySelectorAll('script').forEach((oldScript) => {
    const newScript = document.createElement('script');

    Array.from(oldScript.attributes).forEach((attribute) => {
      newScript.setAttribute(attribute.name, attribute.value);
    });

    if (oldScript.textContent) {
      newScript.textContent = oldScript.textContent;
    }

    oldScript.parentNode?.replaceChild(newScript, oldScript);
  });
};

const swapMainContent = async (url) => {
  if (window.__libernovoProductContextSwitcherLoading) return;
  const currentMain = document.querySelector('#MainContent') || document.querySelector('main');
  if (!currentMain) {
    window.location.href = url;
    return;
  }

  window.__libernovoProductContextSwitcherLoading = true;
  const response = await fetch(url, {
    credentials: 'same-origin',
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
    },
  });

  if (!response.ok) {
    throw new Error(`Request failed: ${response.status}`);
  }

  const html = await response.text();
  const nextDocument = new DOMParser().parseFromString(html, 'text/html');
  const nextMain = nextDocument.querySelector('#MainContent');

  if (!nextMain) {
    throw new Error('Main content not found in response');
  }

  currentMain.innerHTML = nextMain.innerHTML;

  if (nextDocument.title) {
    document.title = nextDocument.title;
  }

  executeScripts(currentMain);

  window.dispatchEvent(
    new CustomEvent('libernovo:product-page-swapped', {
      detail: { url },
    })
  );

  window.__libernovoProductContextSwitcherLoading = false;
};

const getData = (blockEl) => {
  const dataEl = document.getElementById(`product-context-switcher-data-${blockEl.dataset.blockId}`);
  if (!dataEl) return null;

  try {
    return JSON.parse(dataEl.textContent || '{}');
  } catch (error) {
    console.warn('Product context switcher: failed to parse data', error);
    return null;
  }
};

const getCurrentUrlState = () => {
  const params = new URLSearchParams(window.location.search);
  const state = {};
  STATE_KEYS.forEach((key) => {
    const value = params.get(key);
    if (value) state[key] = value;
  });
  return state;
};

const getProductForm = () => {
  return (
    document.querySelector('form[data-type="add-to-cart-form"]') ||
    document.querySelector('product-form-component form') ||
    document.querySelector('form[action*="/cart/add"]')
  );
};

const ensureHiddenInput = (form, name) => {
  if (!form) return null;

  const escapedName = window.CSS && typeof window.CSS.escape === 'function'
    ? window.CSS.escape(name)
    : String(name).replace(/"/g, '\\"');
  const existing = form.querySelector(`input[name="${escapedName}"]`);
  if (existing) return existing;

  const input = document.createElement('input');
  input.type = 'hidden';
  input.name = name;
  form.appendChild(input);
  return input;
};

const getRecords = (data) => {
  if (!Array.isArray(data.catalog)) return [];

  return dedupeByKey(
    data.catalog
    .map((record) => ({
      series: cleanValue(record.series),
      model: cleanValue(record.model),
      colors: splitValues(record.colors),
      batteryCapacities: splitValues(record.batteryCapacities),
    }))
    .filter((record) => Boolean(record.series && record.model)),
    (record) => `${normalize(record.series)}|${normalize(record.model)}|${normalize(record.batteryCapacities.join(','))}|${normalize(record.colors.join(','))}`
  );
};

const getProductColors = (data) => {
  if (!Array.isArray(data.variants)) return [];

  return dedupeByKey(
    data.variants
      .map((variant) => getVariantColor(data, variant))
      .filter(Boolean),
    (value) => getColorBucket(value)
  );
};

const getMatchingColorsForRecord = (record, kind) => {
  return record.colors || [];
};

const getAllowedValues = (records, kind, role, state) => {
  const values = [];

  records.forEach((record) => {
    if (role === 'series') {
      values.push(record.series);
      return;
    }

    if (role === 'model') {
      if (state.series && normalize(record.series) !== normalize(state.series)) return;
      values.push(record.model);
      return;
    }

    if (role === 'capacity') {
      if (state.series && normalize(record.series) !== normalize(state.series)) return;
      if (state.model && normalize(record.model) !== normalize(state.model)) return;
      values.push(...record.batteryCapacities);
      return;
    }

    if (role === 'color') {
      if (state.series && normalize(record.series) !== normalize(state.series)) return;
      if (state.model && normalize(record.model) !== normalize(state.model)) return;
      if (state.capacity && record.batteryCapacities.length) {
        const hasCapacity = record.batteryCapacities.some((capacity) => normalize(capacity) === normalize(state.capacity));
        if (!hasCapacity) return;
      }
      values.push(...getMatchingColorsForRecord(record, kind));
    }
  });

  return dedupeByKey(values.filter(Boolean), (value) => normalize(value));
};

const buildUrlFromResolvedState = (resolved) => {
  const url = new URL(window.location.href);

  if (resolved.kind === 'battery') {
    url.searchParams.delete('series');
  } else if (resolved.series) {
    url.searchParams.set('series', resolved.series);
  } else {
    url.searchParams.delete('series');
  }

  if (resolved.model) {
    url.searchParams.set('model', resolved.model);
  } else {
    url.searchParams.delete('model');
  }

  if (resolved.kind === 'battery' && resolved.capacity) {
    url.searchParams.set('capacity', resolved.capacity);
  } else {
    url.searchParams.delete('capacity');
  }

  if (resolved.color) {
    url.searchParams.set('color', resolved.color);
  } else {
    url.searchParams.delete('color');
  }

  if (resolved.variant) {
    url.searchParams.set('variant', resolved.variant);
  } else {
    url.searchParams.delete('variant');
  }

  return url.toString();
};

const getRoleState = (kind, role, state) => {
  const nextState = { ...state };

  if (role === 'series') {
    delete nextState.model;
    delete nextState.capacity;
    delete nextState.color;
    delete nextState.variant;
  } else if (role === 'model') {
    delete nextState.capacity;
    delete nextState.color;
    delete nextState.variant;
  } else if (role === 'capacity') {
    delete nextState.variant;
  } else if (role === 'color') {
    delete nextState.variant;
  }

  if (kind === 'battery') {
    delete nextState.series;
  }

  return nextState;
};

const findMatchingVariant = (data, state = {}) => {
  if (!Array.isArray(data?.variants) || !data.variants.length) return null;

  const selectedModel = normalizeModel(state.model || '');
  const selectedColorBucket = getColorBucket(state.color || '');
  const selectedCapacity = normalizeCapacity(state.capacity || '');
  const requiresCompatibility = hasCompatibilityOption(data);
  const requiresCapacity = hasCapacityOption(data) && Boolean(selectedCapacity);

  let bestMatch = null;
  let bestScore = -Infinity;

  data.variants.forEach((variant) => {
    const variantColorBucket = getColorBucket(getVariantColor(data, variant));
    if (selectedColorBucket && variantColorBucket !== selectedColorBucket) return;

    const compatibilityTokens = getVariantCompatibilityTokens(data, variant);
    if (requiresCompatibility && selectedModel) {
      if (!compatibilityTokens.includes(selectedModel)) return;
    }

    const variantCapacity = normalizeCapacity(getVariantCapacity(data, variant));
    if (requiresCapacity) {
      if (!variantCapacity || variantCapacity !== selectedCapacity) return;
    }

    let score = 0;
    if (selectedColorBucket && variantColorBucket === selectedColorBucket) score += 200;
    if (selectedModel && compatibilityTokens.includes(selectedModel)) score += 120;
    if (requiresCapacity && variantCapacity === selectedCapacity) score += 60;
    if (variant.available) score += 5;

    if (score > bestScore) {
      bestScore = score;
      bestMatch = variant;
    }
  });

  if (bestMatch) return bestMatch;

  if (!requiresCompatibility) {
    return data.variants.find((variant) => getColorBucket(getVariantColor(data, variant)) === selectedColorBucket && variant.available)
      || data.variants.find((variant) => getColorBucket(getVariantColor(data, variant)) === selectedColorBucket)
      || data.variants.find((variant) => variant.available)
      || data.variants[0]
      || null;
  }

  return null;
};

const getVariantById = (data, variantId) => {
  if (!Array.isArray(data?.variants) || !variantId) return null;

  return data.variants.find((variant) => String(variant.id) === String(variantId)) || null;
};

const getVariantDebugLabel = (variant) => {
  if (!variant) return '';

  const optionValues = [variant.option1, variant.option2, variant.option3]
    .map((value) => cleanValue(value || ''))
    .filter(Boolean);

  return optionValues.join(' + ') || cleanValue(variant.title || '');
};

const getVariantDisplayTitle = (variant) => cleanValue(variant?.title || '') || getVariantDebugLabel(variant);

const debugResolvedVariant = (data, resolved, source = 'selection') => {
  if (!data || !resolved) return;

  const variant = getVariantById(data, resolved.variant || data.currentVariantId);
  if (!variant) return;

  if (typeof window !== 'undefined') {
    window.__libernovoAccessoryDebugStore = window.__libernovoAccessoryDebugStore || {};

    const storeKey = `${String(data.productId || 'unknown')}:${source}`;
    const debugKey = [
      cleanValue(resolved.series || ''),
      cleanValue(resolved.model || ''),
      cleanValue(resolved.capacity || ''),
      cleanValue(resolved.color || ''),
      String(variant.id || ''),
    ].join('|');

    if (window.__libernovoAccessoryDebugStore[storeKey] === debugKey) return;
    window.__libernovoAccessoryDebugStore[storeKey] = debugKey;
  }

  const summary = [
    `[LiberNovo Accessory] ${data.kind || 'accessory'} variant resolved`,
    `model="${resolved.model || ''}"`,
    `color="${resolved.color || ''}"`,
    resolved.capacity ? `capacity="${resolved.capacity}"` : '',
    `matched="${getVariantDisplayTitle(variant)}"`,
    `sku="${cleanValue(variant.sku || '') || '(blank)'}"`,
    `id="${String(variant.id || '')}"`,
  ]
    .filter(Boolean)
    .join(' | ');

  console.log(summary);
  console.group(`[LiberNovo Accessory] ${data.kind || 'accessory'} variant resolved details`);
  console.log('Source:', source);
  console.log('Selected Chair Series:', resolved.series || '');
  console.log('Selected Chair Model:', resolved.model || '');
  if (resolved.capacity) {
    console.log('Selected Capacity:', resolved.capacity);
  }
  console.log('Selected Color:', resolved.color || '');
  console.log('Matched Variant:', getVariantDisplayTitle(variant));
  console.log('Variant SKU:', cleanValue(variant.sku || '') || '(blank)');
  console.log('Variant ID:', variant.id);
  console.log('Variant Title:', variant.title || '');
  console.groupEnd();
};

const resolveState = (data, rawState = {}) => {
  const kind = data.kind || 'footrest';
  const records = getRecords(data);
  const productColors = getProductColors(data);
  const currentVariant = data.variants.find((variant) => String(variant.id) === String(rawState.variant || data.currentVariantId))
    || data.variants[0]
    || null;
  const rawColor = cleanValue(rawState.color || getVariantColor(data, currentVariant) || data.currentVariantColor || '');
  const currentColor = normalize(rawColor);
  const isBattery = kind === 'battery';

  const resolved = {
    series: isBattery ? '' : cleanValue(rawState.series || ''),
    model: cleanValue(rawState.model || ''),
    capacity: cleanValue(rawState.capacity || ''),
    color: currentColor ? cleanValue(currentColor) : '',
    variant: cleanValue(rawState.variant || data.currentVariantId || ''),
  };

  const seriesOptions = isBattery ? [] : getAllowedValues(records, kind, 'series', {});
  if (!isBattery) {
    if (!resolved.series || !seriesOptions.some((value) => normalize(value) === normalize(resolved.series))) {
      resolved.series = seriesOptions[0] || '';
    }
  }

  const modelOptions = getAllowedValues(records, kind, 'model', { series: resolved.series });
  if (!resolved.model || !modelOptions.some((value) => normalize(value) === normalize(resolved.model))) {
    resolved.model = modelOptions[0] || '';
  }

  const capacityOptions = kind === 'battery'
    ? getAllowedValues(records, kind, 'capacity', { model: resolved.model })
    : [];
  if (kind === 'battery' && (!resolved.capacity || !capacityOptions.some((value) => normalize(value) === normalize(resolved.capacity)))) {
    resolved.capacity = capacityOptions[0] || '';
  }

  const colorOptions = getAllowedValues(records, kind, 'color', {
    series: resolved.series,
    model: resolved.model,
    capacity: resolved.capacity,
  });
  const allowedColorBuckets = new Set(colorOptions.map((value) => getColorBucket(value)));
  const availableColors = colorOptions.filter((value) =>
    productColors.some((productColor) => getColorBucket(productColor) === getColorBucket(value))
  );
  const preferredColorCandidates = [
    cleanValue(rawState.color || ''),
    cleanValue(getVariantColor(data, currentVariant) || ''),
    cleanValue(data.currentVariantColor || ''),
  ].filter(Boolean);
  const matchingAllowedColor = preferredColorCandidates.find((candidate) => {
    const candidateBucket = getColorBucket(candidate);
    return (
      allowedColorBuckets.has(candidateBucket) &&
      productColors.some((productColor) => getColorBucket(productColor) === candidateBucket)
    );
  });
  if (!resolved.color || !allowedColorBuckets.has(getColorBucket(resolved.color))) {
    resolved.color = matchingAllowedColor || availableColors[0] || productColors[0] || '';
  }

  const variant = findMatchingVariant(data, resolved);

  if (variant) {
    resolved.variant = String(variant.id);
  }

  return {
    ...resolved,
    kind,
    records,
    seriesOptions,
    modelOptions,
    capacityOptions,
    colorOptions: availableColors,
    productColors,
  };
};

const setFormProperties = (resolved) => {
  const form = getProductForm();
  if (!form) return;

  const fields =
    resolved.kind === 'battery'
      ? ['model', 'capacity', 'color']
      : ['series', 'model', 'color'];

  fields.forEach((key) => {
    const input = ensureHiddenInput(form, `properties[${PROPERTY_INPUTS[key]}]`);
    if (input) {
      input.value = resolved[key] || '';
    }
  });

  const idInput = ensureHiddenInput(form, 'id');
  if (idInput && resolved.variant) {
    idInput.value = resolved.variant;
  }
};

const updateButtonState = (button, active, visible) => {
  button.classList.toggle('is-active', active);
  button.classList.toggle('is-hidden', !visible);
  button.setAttribute('aria-pressed', active ? 'true' : 'false');

  const wrapper = button.closest('.pcs__item');
  if (wrapper) {
    wrapper.hidden = !visible;
  }
};

const isButtonVisible = (button, resolved, data) => {
  const role = button.dataset.role || resolved.role;
  const value = normalize(button.dataset.value || button.dataset.valueKey || '');

  if (role === 'series') {
    return resolved.seriesOptions.some((option) => normalize(option) === value);
  }

  if (role === 'model') {
    return resolved.modelOptions.some((option) => normalize(option) === value);
  }

  if (role === 'capacity') {
    return resolved.capacityOptions.some((option) => normalize(option) === value);
  }

  if (role === 'color') {
    const productColor = getColorBucket(button.dataset.value || '');
    return resolved.colorOptions.some((option) => getColorBucket(option) === productColor);
  }

  return true;
};

const syncBlock = (blockEl) => {
  const data = getData(blockEl);
  if (!data) return;

  const resolved = resolveState(data, getCurrentUrlState());
  debugResolvedVariant(data, resolved, 'selection');
  const role = data.role || blockEl.dataset.accessoryRole || 'series';
  const buttons = Array.from(blockEl.querySelectorAll('.pcs__button'));
  let visibleCount = 0;

  buttons.forEach((button) => {
    const buttonRole = button.dataset.role || role;
    const valueKey = normalize(button.dataset.valueKey || button.dataset.value || '');
    const isVisible = isButtonVisible(button, resolved, data);
    const isActive =
      (buttonRole === 'series' && normalize(resolved.series) === valueKey) ||
      (buttonRole === 'model' && normalize(resolved.model) === valueKey) ||
      (buttonRole === 'capacity' && normalize(resolved.capacity) === valueKey) ||
      (buttonRole === 'color' && getColorBucket(resolved.color) === getColorBucket(button.dataset.value || button.dataset.valueKey || ''));

    updateButtonState(button, isActive, isVisible);
    if (isVisible) visibleCount += 1;
  });

  debugLog('resolved', {
    kind: resolved.kind,
    role,
    productId: data.productId,
    catalogRecords: resolved.records.length,
    seriesOptions: resolved.seriesOptions.length,
    modelOptions: resolved.modelOptions.length,
    capacityOptions: resolved.capacityOptions.length,
    colorOptions: resolved.colorOptions.length,
    resolved,
  });

  if (visibleCount === 0) {
    blockEl.hidden = true;
  } else if (blockEl.dataset.hideIfSingle === 'true') {
    blockEl.hidden = visibleCount <= 1;
  } else {
    blockEl.hidden = false;
  }

  setFormProperties(resolved);
};

const syncGroup = (productId) => {
  document
    .querySelectorAll(`.product-context-switcher[data-product-id="${productId}"]`)
    .forEach((blockEl) => syncBlock(blockEl));
};

const buildNextUrl = (data, currentResolved, role, value) => {
  const next = {
    series: currentResolved.series,
    model: currentResolved.model,
    capacity: currentResolved.capacity,
    color: currentResolved.color,
    variant: currentResolved.variant,
  };

  if (role === 'series') {
    next.series = value;
    next.model = '';
    next.capacity = '';
    next.color = '';
    next.variant = '';
  } else if (role === 'model') {
    next.model = value;
    next.capacity = '';
    next.color = '';
    next.variant = '';
  } else if (role === 'capacity') {
    next.capacity = value;
    next.variant = '';
  } else if (role === 'color') {
    next.color = value;
    const matchedVariant = findMatchingVariant(data, next);
    if (matchedVariant) {
      next.variant = String(matchedVariant.id);
    }
  }

  const resolved = resolveState(data, next);
  return buildUrlFromResolvedState(resolved);
};

const onClick = (event) => {
  if (window.__libernovoProductContextSwitcherLoading) return;
  const button = event.target.closest('[data-product-context-switcher] .pcs__button');
  if (!button) return;

  const blockEl = button.closest('[data-product-context-switcher]');
  const data = getData(blockEl);
  if (!data) return;

  event.preventDefault();
  event.stopPropagation();

  const currentResolved = resolveState(data, getCurrentUrlState());
  const nextUrl = buildNextUrl(data, currentResolved, button.dataset.role || data.role, button.dataset.value || '');
  try {
    const nextUrlObject = new URL(nextUrl, window.location.origin);
    const nextState = {
      series: nextUrlObject.searchParams.get('series') || '',
      model: nextUrlObject.searchParams.get('model') || '',
      capacity: nextUrlObject.searchParams.get('capacity') || '',
      color: nextUrlObject.searchParams.get('color') || '',
      variant: nextUrlObject.searchParams.get('variant') || '',
    };
    const nextResolved = resolveState(data, nextState);
    debugResolvedVariant(data, nextResolved, `${button.dataset.role || data.role || 'selection'}-click`);
  } catch (_) {}

  window.history.replaceState(null, '', nextUrl);
  swapMainContent(nextUrl)
    .then(() => {
      syncGroup(data.productId);
    })
    .catch((error) => {
      window.__libernovoProductContextSwitcherLoading = false;
      console.warn('Product context switcher failed to swap main content, falling back to navigation.', error);
      window.location.href = nextUrl;
    });
};

const initProductContextSwitchers = () => {
  const blocks = Array.from(document.querySelectorAll('[data-product-context-switcher]'));
  if (!blocks.length) return;

  let canonicalUrl = '';
  blocks.forEach((blockEl) => {
    const data = getData(blockEl);
    if (!data || canonicalUrl) return;
    const resolved = resolveState(data, getCurrentUrlState());
    canonicalUrl = buildUrlFromResolvedState(resolved);
  });

  const currentUrl = window.location.href.split('#')[0];
  if (
    canonicalUrl &&
    canonicalUrl !== currentUrl &&
    !window.__libernovoProductContextSwitcherCanonicalizing
  ) {
    window.__libernovoProductContextSwitcherCanonicalizing = true;
    window.history.replaceState(null, '', canonicalUrl);
    swapMainContent(canonicalUrl)
      .then(() => {
        window.__libernovoProductContextSwitcherCanonicalizing = false;
      })
      .catch((error) => {
        window.__libernovoProductContextSwitcherCanonicalizing = false;
        console.warn('Product context switcher: initial canonicalization failed.', error);
      });
    return;
  }

  blocks.forEach((blockEl) => syncBlock(blockEl));
};

if (!window.__libernovoProductContextSwitcherBound) {
  window.__libernovoProductContextSwitcherBound = true;
  document.addEventListener('click', onClick);
  document.addEventListener('DOMContentLoaded', initProductContextSwitchers);
  window.addEventListener('libernovo:product-page-swapped', initProductContextSwitchers);
  window.initProductContextSwitchers = initProductContextSwitchers;
}

initProductContextSwitchers();
